import { useState, useEffect } from 'react';
import { Check, Edit3, Sparkles } from 'lucide-react';

const SpellCheckConfirmation = ({ 
  original, 
  corrected, 
  correctionsSummary,
  onConfirm, 
  onEdit 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in with slight delay
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleConfirm = () => {
    setIsExiting(true);
    setTimeout(() => onConfirm(corrected), 350);
  };

  const handleEdit = () => {
    setIsExiting(true);
    setTimeout(() => onEdit(corrected), 350);
  };

  return (
    <div 
      className={`transition-all duration-400 ease-out ${
        isVisible && !isExiting
          ? 'opacity-100 translate-y-0 scale-100' 
          : 'opacity-0 translate-y-4 scale-95'
      }`}
    >
      <div 
        className="rounded-2xl p-5 border shadow-xl"
        style={{
          background: 'linear-gradient(145deg, rgba(255,255,255,0.98) 0%, rgba(253,248,246,0.95) 50%, rgba(255,228,225,0.9) 100%)',
          borderColor: 'rgba(186,123,124,0.2)',
          boxShadow: '0 20px 40px rgba(186,123,124,0.15), 0 8px 16px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)'
        }}
      >
        {/* Header with sparkle effect */}
        <div className="flex items-center space-x-3 mb-4">
          <div 
            className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #D4A5A5 0%, #BA7B7C 100%)',
              boxShadow: '0 4px 15px rgba(186,123,124,0.4)'
            }}
          >
            <Sparkles className="w-5 h-5 text-white animate-pulse" />
          </div>
          <div>
            <p 
              className="text-base font-semibold text-[#1C1917]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              ¿Quisiste decir?
            </p>
            {correctionsSummary && (
              <p className="text-xs text-[#A8A29E] mt-0.5">{correctionsSummary}</p>
            )}
          </div>
        </div>

        {/* Original vs Corrected */}
        <div className="space-y-3 mb-5">
          {/* Original - crossed out with animation */}
          <div 
            className="rounded-xl p-3.5 border transition-all duration-300 hover:border-red-200"
            style={{
              background: 'linear-gradient(135deg, rgba(254,242,242,0.8) 0%, rgba(254,226,226,0.5) 100%)',
              borderColor: 'rgba(252,165,165,0.3)'
            }}
          >
            <p className="text-xs text-red-400/80 mb-1.5 font-medium">Original:</p>
            <p className="text-sm text-red-400 line-through decoration-2">{original}</p>
          </div>
          
          {/* Corrected - highlighted with glow */}
          <div 
            className="rounded-xl p-3.5 border transition-all duration-300 hover:shadow-md"
            style={{
              background: 'linear-gradient(135deg, rgba(186,123,124,0.08) 0%, rgba(212,165,165,0.12) 100%)',
              borderColor: 'rgba(186,123,124,0.25)',
              boxShadow: '0 2px 8px rgba(186,123,124,0.1)'
            }}
          >
            <p className="text-xs text-[#BA7B7C] mb-1.5 font-medium">✨ Sugerencia:</p>
            <p 
              className="text-sm text-[#1C1917] font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '15px' }}
            >
              &ldquo;{corrected}&rdquo;
            </p>
          </div>
        </div>

        {/* Action Buttons with premium styling */}
        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            className="flex-1 flex items-center justify-center space-x-2 py-3.5 px-5 text-white rounded-xl text-sm font-medium transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #D4A5A5 0%, #BA7B7C 100%)',
              boxShadow: '0 4px 15px rgba(186,123,124,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}
            data-testid="spell-confirm-btn"
          >
            <Check className="w-4 h-4" />
            <span>Sí, usar esta</span>
          </button>
          <button
            onClick={handleEdit}
            className="flex-1 flex items-center justify-center space-x-2 py-3.5 px-5 bg-white border-2 border-[#E7E5E4] text-[#57534E] rounded-xl text-sm font-medium hover:border-[#BA7B7C] hover:text-[#BA7B7C] hover:bg-[#FAFAF9] transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
            style={{
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
            }}
            data-testid="spell-edit-btn"
          >
            <Edit3 className="w-4 h-4" />
            <span>Editar</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpellCheckConfirmation;
